import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import * as filterHelpers from 'app/modules/browse/helpers/filter.helpers';
import {
  BrowseFilter,
  FilterNamesEnum,
} from 'app/modules/browse/models/filter';
import { FilterOption } from 'app/modules/browse/models/filter-options';
import { Sport } from 'app/modules/browse/models/sport';
import { FilterService } from 'app/modules/browse/services/filter.service';
import { TranslateModule } from '@ngx-translate/core';
import { FilterSliderComponent } from '../../filter-slider/filter-slider.component';
import { NgTemplateOutlet } from '@angular/common';
import { Unit } from '@kinomap-dev/ng-ui-lib';

@Component({
  selector: 'app-all-filters-field',
  templateUrl: './all-filters-field.component.html',
  styleUrls: ['./all-filters-field.component.scss'],
  standalone: true,
  imports: [NgTemplateOutlet, FilterSliderComponent, TranslateModule],
})
export class AllFiltersFieldComponent implements OnChanges {
  private filterService: FilterService = inject(FilterService);

  @Input() unit: Unit = 'metric';
  @Input() filter!: BrowseFilter;
  @Input() sport!: Sport;
  @Input() filtersCleared!: boolean;

  @Output() changeSelectedOptions: EventEmitter<{
    filterName: FilterNamesEnum;
    selectedOptions: FilterOption[];
  }> = new EventEmitter<{
    filterName: FilterNamesEnum;
    selectedOptions: FilterOption[];
  }>();

  filterHelpers = filterHelpers;
  filterOptions: FilterOption[] = [];
  selectedOptions: FilterOption[] = [];
  customSelectedOptions: FilterOption[] = [];
  private customFilterOption?: FilterOption;

  get isSlider(): boolean {
    const sliderFilterNames: FilterNamesEnum[] =
      filterHelpers.getFilterNamesWithSlider();
    return sliderFilterNames.includes(this.filter.name);
  }

  get hasCustomSliderOption(): boolean {
    return filterHelpers.filterHasCustomSliderOption(this.filter.name);
  }

  isCustomOption(option: FilterOption): boolean {
    return filterHelpers.isCustomFilterOption(option);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !!changes.filter ||
      (!!changes.sport && this.filter.name === FilterNamesEnum.WorkoutType)
    ) {
      this.filterOptions = this.filterService.getFilterOptions(
        this.filter,
        this.sport
      );
      this.selectedOptions = [...this.filter.selectedOptions];
      this.initializeCustomSliderState();

      this.changeSelectedOptions.next({
        filterName: this.filter.name,
        selectedOptions: this.selectedOptions,
      });
    }

    if (!!changes.filtersCleared && this.filtersCleared) {
      this.selectedOptions = filterHelpers.getDefaultSelectedOptions(
        this.filter.name
      );
      this.initializeCustomSliderState();

      this.changeSelectedOptions.next({
        filterName: this.filter.name,
        selectedOptions: this.selectedOptions,
      });
    }
  }

  onCheckboxChange(event: any, option: FilterOption): void {
    const isCustom: boolean = this.isCustomOption(option);
    const isAlreadySelected: boolean = filterHelpers.isOptionSelected(
      option,
      this.selectedOptions
    );

    if (event.target.checked && !isAlreadySelected) {
      if (isCustom) {
        const optionToAdd: FilterOption | null =
          this.getCurrentCustomSelection();
        this.selectedOptions = optionToAdd ? [optionToAdd] : [];
      } else {
        this.removeCustomSelections();
        this.selectedOptions.push(option);
      }

      if (
        this.filterOptions.length > 1 &&
        this.selectedOptions.length === this.filterOptions.length &&
        !filterHelpers.canFilterOptionsAllBeSelected(this.filter.name)
      ) {
        event.target.checked = false;
        this.selectedOptions = [];
      }
    } else if (!event.target.checked) {
      if (isCustom) {
        this.selectedOptions = this.selectedOptions.filter(
          (selectedOption: FilterOption) => {
            return !filterHelpers.isCustomSelectionForFilter(
              selectedOption,
              this.filter.name,
              this.filterOptions
            );
          }
        );
      } else {
        this.selectedOptions = this.selectedOptions.filter(
          (o: FilterOption) => o.value !== option.value
        );
      }
    }

    this.changeSelectedOptions.next({
      filterName: this.filter.name,
      selectedOptions: this.selectedOptions,
    });
  }

  onRadioChange(option: FilterOption): void {
    this.selectedOptions = [option];
    this.changeSelectedOptions.next({
      filterName: this.filter.name,
      selectedOptions: this.selectedOptions,
    });
  }

  onSliderChange(): void {
    if (this.hasCustomSliderOption) {
      this.customSelectedOptions = this.customSelectedOptions.map(
        (option: FilterOption) => {
          return { ...option, isCustom: true };
        }
      );
      const currentCustomSelection: FilterOption | null =
        this.getCurrentCustomSelection();

      if (currentCustomSelection) {
        const hasCustomSelected: boolean = this.selectedOptions.some(
          (option: FilterOption) => {
            return filterHelpers.isCustomSelectionForFilter(
              option,
              this.filter.name,
              this.filterOptions
            );
          }
        );

        if (hasCustomSelected) {
          this.selectedOptions = [{ ...currentCustomSelection }];
        }
      }
    }

    this.changeSelectedOptions.next({
      filterName: this.filter.name,
      selectedOptions: this.selectedOptions,
    });
  }

  private initializeCustomSliderState(): void {
    if (!this.hasCustomSliderOption) {
      this.customFilterOption = undefined;
      this.customSelectedOptions = [];
      return;
    }

    this.customFilterOption =
      this.filterOptions.find((option: FilterOption) => {
        return this.isCustomOption(option);
      }) ?? filterHelpers.getCustomSliderDefaultOption(this.filter.name);

    const existingCustomSelection: FilterOption | undefined =
      this.selectedOptions.find((option: FilterOption) => {
        return filterHelpers.isCustomSelectionForFilter(
          option,
          this.filter.name,
          this.filterOptions
        );
      });

    const baseSelection: FilterOption | undefined =
      existingCustomSelection ?? this.customFilterOption;

    this.customSelectedOptions = baseSelection
      ? [{ ...baseSelection, isCustom: true }]
      : [];

    if (existingCustomSelection) {
      const customSelection: FilterOption = {
        ...this.customSelectedOptions[0],
      };
      this.selectedOptions = [customSelection];
    }
  }

  private getCurrentCustomSelection(): FilterOption | null {
    if (!this.hasCustomSliderOption || !this.customSelectedOptions.length)
      return null;
    return { ...this.customSelectedOptions[0], isCustom: true };
  }

  private removeCustomSelections(): void {
    this.selectedOptions = this.selectedOptions.filter(
      (option: FilterOption) => {
        return !filterHelpers.isCustomSelectionForFilter(
          option,
          this.filter.name,
          this.filterOptions
        );
      }
    );
  }
}
